import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { templateMetadataSchema, type TemplateMetadataFormValues } from '../schemas/documentTemplate.schema';
import type { DocumentTemplateDetail } from '../types/documentTemplates.types';
import { applyServerFieldErrors } from '../utils/formErrors';
import { useUpdateTemplateMutation } from './useTemplateMutations';

const toFormValues = (template: DocumentTemplateDetail): TemplateMetadataFormValues => ({
  name: template.name,
  description: template.description ?? '',
  documentType: template.documentType,
});

export function useTemplateMetadataForm(template: DocumentTemplateDetail) {
  const form = useForm<TemplateMetadataFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(templateMetadataSchema),
    defaultValues: toFormValues(template),
  });
  const mutation = useUpdateTemplateMutation(template.id);
  const { reset } = form;

  useEffect(() => {
    reset(toFormValues(template), { keepDirtyValues: true });
  }, [template, reset]);

  const submit = form.handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: (saved) => {
        reset(toFormValues(saved));
        toast.success('Template details saved');
      },
      onError: (error) => {
        if (!applyServerFieldErrors(error, form.setError)) {
          toast.error((error as Error).message);
        }
      },
    });
  });

  return { form, submit, discard: () => reset(), isDirty: form.formState.isDirty, isSaving: mutation.isPending };
}
